import React, { useEffect, useMemo, useRef } from 'react'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

import { DatePicker } from '../DatePicker'

type BaseDPProps = React.ComponentProps<typeof DatePicker>

type NormalizedDatePickerProps = Omit<BaseDPProps, 'value' | 'onChange'> & {
  value?: Date | null
  onChange?: (date: Date | null, dateString: string) => void
  onInvalidText?: (message: string) => void
  onValidText?: () => void
  picker?: 'date' | 'month'
}

/** 全角→半角 & 空白除去 */
const toHalfWidth = (s: string) => s.normalize('NFKC').replace(/\s+/g, '')

/** Day.js っぽいオブジェクトを Date に変換（antd が dayjs を返す場合に対応） */
type DayjsLike = { toDate: () => Date }
const isDayjsLike = (v: unknown): v is DayjsLike =>
  typeof v === 'object' &&
  v !== null &&
  'toDate' in v &&
  typeof v.toDate === 'function'

const toDate = (v: unknown): Date | null => {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v
  if (isDayjsLike(v)) {
    const d = v.toDate()
    return d instanceof Date && !isNaN(d.getTime()) ? d : null
  }
  return null
}

/** YYYY, MM, DD を安全に Date に（存在しない日付は無効） */
function ymdToDate(y: number, m: number, d: number): Date | null {
  const dt = new Date(y, m - 1, d)
  if (
    !isNaN(dt.getTime()) &&
    dt.getFullYear() === y &&
    dt.getMonth() === m - 1 &&
    dt.getDate() === d
  ) {
    // 0時0分0秒に正規化
    dt.setHours(0, 0, 0, 0)
    return dt
  }
  return null
}

/** 入力文字列を YYYY-MM-DD に正規化（YYYYMMDD / 区切り: -, /, . を許容） */
function normalizeToYMD(raw: string): { date: Date; text: string } | null {
  const s = toHalfWidth(raw).trim()

  // 8桁（YYYYMMDD）
  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mm = parseInt(m[2], 10)
    const dd = parseInt(m[3], 10)
    const dt = ymdToDate(y, mm, dd)
    if (dt) return { date: dt, text: formatYMD(dt) }
    return null
  }

  // 区切り（-, /, .）付き
  m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mm = parseInt(m[2], 10)
    const dd = parseInt(m[3], 10)
    const dt = ymdToDate(y, mm, dd)
    if (dt) return { date: dt, text: formatYMD(dt) }
    return null
  }

  return null
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`)
const formatYMD = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

/** ---- month(YYYY-MM) 用 ---- */
const toMonthStart = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(1)
  return x
}
function ymToDate(y: number, m: number): Date | null {
  if (m < 1 || m > 12) return null
  const dt = new Date(y, m - 1, 1)
  if (
    !isNaN(dt.getTime()) &&
    dt.getFullYear() === y &&
    dt.getMonth() === m - 1
  ) {
    dt.setHours(0, 0, 0, 0)
    return dt
  }
  return null
}
const formatYM = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
function normalizeToYM(raw: string): { date: Date; text: string } | null {
  const s = toHalfWidth(raw).trim()

  // 6桁（YYYYMM）
  let m = s.match(/^(\d{4})(\d{2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mm = parseInt(m[2], 10)
    const dt = ymToDate(y, mm)
    if (dt) return { date: dt, text: `${y}-${pad2(mm)}` }
    return null
  }

  // 区切り（-, /, .）付き
  m = s.match(/^(\d{4})[-/.](\d{1,2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mm = parseInt(m[2], 10)
    const dt = ymToDate(y, mm)
    if (dt) return { date: dt, text: `${y}-${pad2(mm)}` }
    return null
  }

  return null
}

/** ---- 共通：範囲チェック ---- */
function isAllowedByRange(
  d: Date,
  picker: 'date' | 'month',
  minDate?: Date,
  maxDate?: Date
) {
  const t = new Date(d)
  t.setHours(0, 0, 0, 0)

  if (picker === 'month') {
    const tt = toMonthStart(t)
    if (minDate) {
      const mn = toMonthStart(minDate)
      if (tt < mn) return false
    }
    if (maxDate) {
      const mx = toMonthStart(maxDate)
      if (tt > mx) return false
    }
    return true
  } else {
    if (minDate) {
      const mn = new Date(minDate)
      mn.setHours(0, 0, 0, 0)
      if (t < mn) return false
    }
    if (maxDate) {
      const mx = new Date(maxDate)
      mx.setHours(0, 0, 0, 0)
      if (t > mx) return false
    }
    return true
  }
}

export function NormalizedDatePicker(props: NormalizedDatePickerProps) {
  const {
    value,
    onChange,
    onInvalidText,
    onValidText,
    minDate,
    maxDate,
    picker = 'date',
    format: displayFormat,
    ...rest
  } = props

  const resolvedFormat =
    displayFormat ?? (picker === 'month' ? 'YYYY/MM' : 'YYYY-MM-DD')

  const isPointerDownCalenderCellRef = useRef(false)
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest('.ant-picker-cell')) {
        isPointerDownCalenderCellRef.current = true
      }
    }
    const handlePointerUp = () => {
      isPointerDownCalenderCellRef.current = false
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('pointerup', handlePointerUp, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('pointerup', handlePointerUp, true)
    }
  }, [])

  // パネル操作（カレンダー選択）時のハンドラ
  const handleChange = useMemo(() => {
    return (date: unknown, dateString: string | string[]) => {
      const d0 = toDate(date)
      if (d0) {
        const normalized = picker === 'month' ? toMonthStart(d0) : new Date(d0)
        normalized.setHours(0, 0, 0, 0)
        if (!isAllowedByRange(normalized, picker, minDate, maxDate)) {
          onInvalidText?.(
            picker === 'month'
              ? VALIDATION_MESSAGES.INVALID_MONTH_SELECTION
              : VALIDATION_MESSAGES.INVALID_DATE_SELECTION
          )
          onChange?.(null, '')
          return
        }
        onValidText?.()
        const text =
          picker === 'month' ? formatYM(normalized) : formatYMD(normalized)
        onChange?.(normalized, text)
      } else {
        onChange?.(null, typeof dateString === 'string' ? dateString : '')
      }
    }
  }, [onChange, onValidText, onInvalidText, minDate, maxDate, picker])

  // テキスト入力後の正規化（blur）時のハンドラ
  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    if (isPointerDownCalenderCellRef.current) {
      // 以下の問題があったため、カレンダーセルクリック時は blur 処理をスキップする
      // 1. 別の月の日付でマウスダウン
      //   ⇒ ここでテキストボックスからフォーカスがはずれ onBlur 発火
      // 2. onBlur 内の処理により内部で選択日が切り替わりカレンダーが選択月に切り替わる
      //   ⇒ このときマウスポインターが載っている日付が変わる
      // 3. そのままマウスアップで onChange が発火
      //   ⇒ マウスポインターが載っている別日で上書きされる
      return
    }
    // 入力要素を取得
    const maybeInput = e.target as HTMLInputElement | null
    const inputEl =
      (maybeInput && 'value' in maybeInput ? maybeInput : null) ||
      (e.currentTarget as HTMLElement).querySelector('input')

    const raw = (inputEl?.value ?? '').trim()
    if (!raw) return

    const normalized =
      picker === 'month' ? normalizeToYM(raw) : normalizeToYMD(raw)

    if (!normalized) {
      if (inputEl) inputEl.value = ''
      onInvalidText?.(
        picker === 'month'
          ? VALIDATION_MESSAGES.MONTH_INPUT_FORMAT
          : VALIDATION_MESSAGES.DATE_INPUT_FORMAT
      )
      return
    }

    if (!isAllowedByRange(normalized.date, picker, minDate, maxDate)) {
      if (inputEl) inputEl.value = ''
      onInvalidText?.(
        picker === 'month'
          ? VALIDATION_MESSAGES.INVALID_MONTH_SELECTION
          : VALIDATION_MESSAGES.INVALID_DATE_SELECTION
      )
      return
    }

    // 表示は YYYY-MM-DD に統一
    if (inputEl) inputEl.value = normalized.text
    onChange?.(normalized.date, normalized.text)
    onValidText?.()
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.key === 'Enter') {
      // Enter submit 前に正規化を実行
      handleBlur(e as unknown as React.FocusEvent<HTMLElement>)
    }
  }

  return (
    <DatePicker
      {...rest}
      picker={picker}
      format={resolvedFormat}
      value={value ?? null}
      onChange={handleChange}
      onBlur={handleBlur}
      minDate={minDate}
      maxDate={maxDate}
      onKeyDown={handleKeyDown}
    />
  )
}
